import { useEffect, useId, useRef, type ComponentPropsWithRef, type ReactNode, type SelectHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, CheckCircle2, Info, Loader2, Minus, Plus, XCircle } from 'lucide-react';
import type { EsitoComando, MotivoFallimento } from '@emacomfort/domain';
import s from './primitives.module.css';

export { s as stili };

export function cx(...classi: Array<string | false | null | undefined>): string {
  return classi.filter(Boolean).join(' ');
}

// ───────────────────────────── Card ─────────────────────────────

export function Card({ titolo, icona, azione, children, className }: { titolo?: ReactNode; icona?: ReactNode; azione?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx(s.card, className)}>
      {(titolo || azione) && (
        <header className={s.riga}>
          {titolo && (
            <h2 className={s.cardTitolo}>
              {icona}
              {titolo}
            </h2>
          )}
          <span className={s.spazio} />
          {azione}
        </header>
      )}
      {children}
    </section>
  );
}

// ───────────────────────────── Bottone ─────────────────────────────

type VarianteBottone = 'base' | 'primario' | 'pericolo' | 'fantasma';

export function Bottone({
  variante = 'base',
  quadrato,
  pieno,
  className,
  children,
  ...rest
}: ComponentPropsWithRef<'button'> & { variante?: VarianteBottone; quadrato?: boolean; pieno?: boolean }) {
  return (
    <button
      type="button"
      className={cx(s.bottone, variante === 'primario' && s.primario, variante === 'pericolo' && s.pericolo, variante === 'fantasma' && s.fantasma, quadrato && s.quadrato, pieno && s.pieno, className)}
      {...rest}
    >
      {children}
    </button>
  );
}

// ───────────────────────────── Segmenti (scelta fra opzioni, 1 tocco) ─────────────────────────────

export function Segmenti<T extends string>({
  opzioni,
  valore,
  onChange,
  etichetta,
  disabilitato,
  colonne,
}: {
  opzioni: Array<{ valore: T; etichetta: ReactNode; icona?: ReactNode }>;
  valore: T | null;
  onChange: (v: T) => void;
  etichetta: string;
  disabilitato?: boolean;
  /** Griglia a N colonne invece della riga flessibile (es. 2×2 per le quattro modalità). */
  colonne?: number;
}) {
  const stile = colonne ? { display: 'grid', gridTemplateColumns: `repeat(${colonne}, minmax(0, 1fr))` } : undefined;
  return (
    <div role="group" aria-label={etichetta} className={s.segmenti} style={stile}>
      {opzioni.map((o) => {
        const attivo = o.valore === valore;
        return (
          <button key={o.valore} type="button" className={cx(s.segmento, attivo && s.segmentoAttivo)} aria-pressed={attivo} onClick={() => onChange(o.valore)} disabled={disabilitato}>
            {attivo ? <Check size={22} className={s.spunta} aria-hidden /> : o.icona}
            {o.etichetta}
          </button>
        );
      })}
    </div>
  );
}

// ───────────────────────────── Interruttore (stato con testo, mai solo colore) ─────────────────────────────

export function Interruttore({
  acceso,
  onChange,
  etichetta,
  testoAcceso,
  testoSpento,
  disabilitato,
}: {
  acceso: boolean;
  onChange: (v: boolean) => void;
  etichetta: string;
  testoAcceso: string;
  testoSpento: string;
  disabilitato?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={acceso}
      aria-label={etichetta}
      className={cx(s.interruttore, acceso ? s.interruttoreAcceso : s.interruttoreSpento)}
      onClick={() => onChange(!acceso)}
      disabled={disabilitato}
    >
      <span className={s.interruttoreBinario} aria-hidden />
      <span className={s.interruttoreTesto}>{acceso ? testoAcceso : testoSpento}</span>
    </button>
  );
}

// ───────────────────────────── Stepper ± ─────────────────────────────

export function Stepper({
  valore,
  onMeno,
  onPiu,
  etichettaMeno,
  etichettaPiu,
  disabilitato,
  dimensione = 'secondario',
  obsoleto,
}: {
  valore: ReactNode;
  onMeno: () => void;
  onPiu: () => void;
  etichettaMeno: string;
  etichettaPiu: string;
  disabilitato?: boolean;
  dimensione?: 'primario' | 'secondario' | 'terziario';
  obsoleto?: boolean;
}) {
  const classeValore = dimensione === 'primario' ? s.valorePrimario : dimensione === 'terziario' ? s.valoreTerziario : s.valoreSecondario;
  return (
    <div className={s.stepper}>
      <Bottone quadrato aria-label={etichettaMeno} onClick={onMeno} disabled={disabilitato}>
        <Minus size={30} aria-hidden />
      </Bottone>
      <span className={cx(classeValore, s.stepperValore, obsoleto && s.valoreObsoleto)} aria-live="polite">
        {valore}
      </span>
      <Bottone quadrato aria-label={etichettaPiu} onClick={onPiu} disabled={disabilitato}>
        <Plus size={30} aria-hidden />
      </Bottone>
    </div>
  );
}

// ───────────────────────────── Valore con unità ─────────────────────────────

export function Valore({
  numero,
  unita,
  dimensione = 'primario',
  obsoleto,
  className,
}: {
  numero: string;
  unita?: string;
  dimensione?: 'primario' | 'secondario' | 'terziario';
  obsoleto?: boolean;
  className?: string;
}) {
  const classe = dimensione === 'primario' ? s.valorePrimario : dimensione === 'secondario' ? s.valoreSecondario : s.valoreTerziario;
  // La sottolineatura tratteggiata segnala "questo numero non e' attuale"
  // senza affidarsi al solo colore (RNF-04). Su un valore ASSENTE non ha nulla
  // da segnalare — i trattini dicono gia' tutto — e a corpo grande diventava
  // una fila di puntini sotto "— —" che sembrava un artefatto grafico.
  const haNumero = /\d/.test(numero);
  return (
    <span className={cx(classe, obsoleto && haNumero && s.valoreObsoleto, className)}>
      {numero}
      {unita && <span className={s.unita}>{unita}</span>}
    </span>
  );
}

// ───────────────────────────── Badge ─────────────────────────────

export type TonoBadge = 'ok' | 'warn' | 'err' | 'info' | 'neutro' | 'accento';

export function Badge({ tono = 'neutro', icona, children, className }: { tono?: TonoBadge; icona?: ReactNode; children: ReactNode; className?: string }) {
  const classe = { ok: s.badgeOk, warn: s.badgeWarn, err: s.badgeErr, info: s.badgeInfo, neutro: s.badgeNeutro, accento: s.badgeAccento }[tono];
  return (
    <span className={cx(s.badge, classe, className)}>
      {icona}
      {children}
    </span>
  );
}

// ───────────────────────────── Avviso in linea ─────────────────────────────

export function Avviso({ tono = 'info', children, className }: { tono?: 'info' | 'warn' | 'err' | 'ok'; children: ReactNode; className?: string }) {
  const icona = tono === 'warn' || tono === 'err' ? <AlertTriangle size={22} aria-hidden /> : tono === 'ok' ? <CheckCircle2 size={22} aria-hidden /> : <Info size={22} aria-hidden />;
  const classe = { info: s.avvisoInfo, warn: s.avvisoWarn, err: s.avvisoErr, ok: s.avvisoOk }[tono];
  return (
    <div role={tono === 'err' ? 'alert' : 'status'} className={cx(s.avviso, classe, className)}>
      {icona}
      <div>{children}</div>
    </div>
  );
}

// ───────────────────────────── Stato comando (RF-14: tre stati + fallimento) ─────────────────────────────

export function StatoComandoVista({ esito, motivo }: { esito: EsitoComando | undefined; motivo?: MotivoFallimento }) {
  const { t } = useTranslation();
  if (!esito) return <span className={s.statoComando} aria-hidden />;
  const classe = { in_invio: s.statoComandoInInvio, accettato: s.statoComandoAccettato, applicato: s.statoComandoApplicato, fallito: s.statoComandoFallito }[esito];
  const icona = esito === 'in_invio' ? <Loader2 size={16} className={s.spinner} aria-hidden /> : esito === 'accettato' ? <Check size={16} aria-hidden /> : esito === 'applicato' ? <CheckCircle2 size={16} aria-hidden /> : <XCircle size={16} aria-hidden />;
  const testo = { in_invio: t('comando.inInvio'), accettato: t('comando.accettato'), applicato: t('comando.applicato'), fallito: t('comando.fallito') }[esito];
  return (
    <span className={cx(s.statoComando, classe)} role="status">
      {icona}
      {testo}
      {esito === 'fallito' && motivo && <span className={s.muto}> · {t(`comando.motivo.${motivo}`)}</span>}
    </span>
  );
}

// ───────────────────────────── Campo / Select ─────────────────────────────

export function Campo({ etichetta, children, nota }: { etichetta: string; children: ReactNode; nota?: ReactNode }) {
  return (
    <label className={s.campo}>
      <span className={s.etichetta}>{etichetta}</span>
      {children}
      {nota && <span className={cx(s.muto, s.piccolo)}>{nota}</span>}
    </label>
  );
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx(s.select, className)} {...rest}>
      {children}
    </select>
  );
}

// ───────────────────────────── Finestra di conferma (RNF-07) ─────────────────────────────

export function Conferma({
  aperta,
  titolo,
  children,
  onConferma,
  onAnnulla,
  testoConferma,
  pericolosa,
}: {
  aperta: boolean;
  titolo: string;
  children?: ReactNode;
  onConferma: () => void;
  onAnnulla: () => void;
  testoConferma?: string;
  pericolosa?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const primo = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (aperta) primo.current?.focus();
  }, [aperta]);
  if (!aperta) return null;
  return (
    <div className={s.velo} onClick={onAnnulla}>
      <div role="dialog" aria-modal="true" aria-labelledby={id} className={s.finestra} onClick={(e) => e.stopPropagation()}>
        <h2 id={id} className={s.finestraTitolo}>
          {titolo}
        </h2>
        {children && <div>{children}</div>}
        <div className={s.finestraAzioni}>
          <Bottone ref={primo} onClick={onAnnulla}>
            {t('comune.annulla')}
          </Bottone>
          <Bottone variante={pericolosa ? 'pericolo' : 'primario'} onClick={onConferma}>
            {testoConferma ?? t('comune.conferma')}
          </Bottone>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────── Avvisi (toast) ─────────────────────────────

export function Avvisi({ avvisi }: { avvisi: Array<{ id: number; testo: string; tono: 'ok' | 'errore' | 'info' }> }) {
  if (avvisi.length === 0) return null;
  return (
    <div className={s.avvisiContenitore} aria-live="polite">
      {avvisi.map((a) => (
        <div key={a.id} className={cx(s.avvisoToast, a.tono === 'ok' && s.avvisoToastOk, a.tono === 'errore' && s.avvisoToastErrore)}>
          {a.testo}
        </div>
      ))}
    </div>
  );
}

export function Pagina({ titolo, sottotitolo, azione, children }: { titolo: string; sottotitolo?: ReactNode; azione?: ReactNode; children: ReactNode }) {
  return (
    <div className={s.pagina}>
      <header className={s.riga}>
        <div>
          <h1 className={s.paginaTitolo}>{titolo}</h1>
          {sottotitolo && <p className={s.paginaSottotitolo}>{sottotitolo}</p>}
        </div>
        <span className={s.spazio} />
        {azione}
      </header>
      {children}
    </div>
  );
}
